import { motion } from "framer-motion";
import clsx from "clsx";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "units/dashboard/context/LanguageProvider";
import { RTL_LANGUAGES } from "i18n";

/* رنگ آیکن + کارت بر اساس tone */
const TONES = {
  "teal-strong": {
    card: "bg-gradient-to-br from-teal to-teal-700 text-white border-transparent",
    title: "text-white",
    desc: "text-white/85",
    iconBg: "bg-white/15 text-white",
    arrow: "text-gold-2",
    glow: "bg-gold-2/40",
  },
  teal: {
    card: "bg-surface border-line text-ink",
    title: "text-ink",
    desc: "text-ink-soft",
    iconBg: "bg-teal-soft text-teal dark:bg-teal-2/15 dark:text-teal-3",
    glow: "bg-teal-2/25",
  },
  gold: {
    card: "bg-surface border-line text-ink",
    title: "text-ink",
    desc: "text-ink-soft",
    iconBg: "bg-gold-soft text-amber-700 dark:bg-gold-2/15 dark:text-gold-2",
    glow: "bg-gold-2/25",
  },
  pink: {
    card: "bg-surface border-line text-ink",
    title: "text-ink",
    desc: "text-ink-soft",
    iconBg: "bg-pink-100 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300",
    glow: "bg-pink-400/20",
  },
};

export default function BentoCard({ feature, index = 0 }) {
  const { t } = useTranslation("landing");
  const { lang } = useLanguage();
  const isRTL = RTL_LANGUAGES.includes(lang);

  const { key, Icon, span, tone, featured, hasArrow } = feature;
  const styles = TONES[tone] || TONES.teal;

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{
        delay: index * 0.08,
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
      }}
      className={clsx(
        "group relative overflow-hidden rounded-card border p-6 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-card-lg sm:p-7 lg:p-8",
        styles.card,
        span
      )}
    >
      {/* Glow hover */}
      <div
        aria-hidden
        className={clsx(
          "pointer-events-none absolute -top-16 -end-16 h-52 w-52 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100",
          styles.glow
        )}
      />

      {/* Icon */}
      <div
        className={clsx(
          "relative z-10 mb-5 grid h-14 w-14 place-items-center rounded-2xl transition-transform duration-500 ease-snap group-hover:scale-110 group-hover:-rotate-6 sm:h-16 sm:w-16",
          styles.iconBg
        )}
      >
        <Icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2} />
      </div>

      {/* Content */}
      <h3
        className={clsx(
          "relative z-10 mb-2.5 text-[18px] font-extrabold leading-tight tracking-tight sm:text-[20px]",
          styles.title
        )}
      >
        {t(`feat.${key}.title`)}
      </h3>
      <p
        className={clsx(
          "relative z-10 text-[14px] leading-[1.9] sm:text-[14.5px]",
          styles.desc
        )}
      >
        {t(`feat.${key}.desc`)}
      </p>
    </motion.div>
  );
}